/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import AssertionError from 'assertion-error';
import EventEmitter from 'eventemitter3';

import { neverThrow } from '../../../util.type';
import BasicController from './basic-controller';
import HiddenController from './hidden-controller';
import ModernController from './modern-controller';
import UniversalController from './universal-controller';

/**
 * @import { ViewerManagerController, ViewerManagerEmitter } from '.'
 * @import { IAnalyticsTracker } from '../../analytics'
 * @import { AppManager } from '../../app'
 * @import { Content } from '../../content/content'
 * @import { ContentControllerFactory } from '../../content/controller/'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { ContentTaskCronFactory } from '../../content/task/cron/content-task-cron-factory'
 * @import { Logger } from '../../logger/logger'
 */

/**
 * @implements {ViewerManagerEmitter}
 */
export default class ViewerManager {
  /**
   * @param {IAnalyticsTracker} analyticsTracker
   * @param {AppManager} appManager
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentTaskCronFactory} contentTaskCronFactory
   * @param {Logger} logger
   */
  constructor(
    analyticsTracker,
    appManager,
    contentControllerFactory,
    contentTaskCronFactory,
    logger
  ) {
    this.analyticsTracker = analyticsTracker;
    this.appManager = appManager;
    this.contentControllerFactory = contentControllerFactory;
    this.contentTaskCronFactory = contentTaskCronFactory;
    this.logger = logger;

    /** @type {ViewerManagerController[]} */
    this.controllers = [];

    /** @type {HTMLElement | undefined} */
    this.el = undefined;

    /** @private @type {EventEmitter} */
    this.emitter = new EventEmitter();

    this.onContent = this.onContent.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
  }

  /**
   * @param {HTMLElement} el
   * @param {ContentInfo} contentInfo
   * @param {string} target
   * @returns {ViewerManagerController}
   */
  createHiddenController(el, contentInfo, target) {
    return new HiddenController(
      this.analyticsTracker,
      this.contentControllerFactory,
      contentInfo,
      this.contentTaskCronFactory,
      el,
      this.logger,
      target
    );
  }

  /** @type {ViewerManagerEmitter['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {ViewerManagerEmitter['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * @private
   * @param {Content | null} content
   */
  onContent(content) {
    this.emitter.emit('content', content);
  }

  /**
   * @private
   * @param {Content | null} content
   */
  onPlaying(content) {
    this.emitter.emit('playing', content);
  }

  /**
   * Nastaví novou sadu aktuálně přehrávaných obsahových úloh.
   * Pokud je již nějaká existující sada aktuálně přehrávaných
   * obsahových úloh, tak je nejprve odstraní a zastaví jejich
   * přehrávání, stejně jako při nil contentInfo.
   *
   * Nepřehrává se více úloh zároveň, ale mohou se střídat v čase,
   * což zajišťuje CRON, který se zároveň spustí.
   * @param {ContentInfo | null | undefined} contentInfo
   */
  async setContentInfo(contentInfo) {
    if (!this.el) {
      throw new AssertionError('Root element is not set.');
    }
    this.logger.debug(
      `ViewerManager#setContentInfo ${contentInfo?.contentTimestamp}`
    );
    if (this.controllers.length > 0) {
      await Promise.all(
        this.controllers.map((c) => {
          c.off('content', this.onContent);
          c.off('playing', this.onPlaying);
          return c.stop();
        })
      );
      this.controllers = [];
    }

    if (!contentInfo) {
      this.onContent(null);
      return;
    }

    const hiddenTarget = 'hidden';
    if (hasContentTasksWithTarget(contentInfo, hiddenTarget)) {
      this.controllers.push(
        this.createHiddenController(this.el, contentInfo, hiddenTarget)
      );
    }
    /** @type {ViewerManagerController | undefined} */
    let controller;
    const { type } = contentInfo.visualController;
    switch (type) {
      case 'BasicController':
        controller = new BasicController(
          this.analyticsTracker,
          this.contentControllerFactory,
          contentInfo,
          this.contentTaskCronFactory,
          this.el,
          new EventEmitter(),
          this.logger
        );
        break;
      case 'UniversalController':
        controller = new UniversalController(
          this.analyticsTracker,
          this.appManager,
          this.contentControllerFactory,
          contentInfo,
          this.contentTaskCronFactory,
          this.el,
          new EventEmitter(),
          this.logger
        );
        break;
      case 'ModernController':
        controller = new ModernController(
          this.analyticsTracker,
          this.appManager,
          this.contentControllerFactory,
          contentInfo,
          this.contentTaskCronFactory,
          this.el,
          new EventEmitter(),
          this.logger
        );
        break;
      default:
        neverThrow(type, () => new RangeError(`Unknown controller "${type}".`));
    }
    this.controllers.push(controller);

    await Promise.all(
      this.controllers.map((c) => {
        c.on('content', this.onContent);
        c.on('playing', this.onPlaying);
        return c.start();
      })
    );
  }

  /**
   * @param {HTMLElement | undefined} el
   */
  setEl(el) {
    this.el = el;
  }
}

/**
 * @param {ContentInfo} contentInfo
 * @param {string} target
 * @returns {boolean}
 */
function hasContentTasksWithTarget(contentInfo, target) {
  return (
    contentInfo.contentTasks.findIndex(
      (contentTask) => contentTask.target === target
    ) !== -1
  );
}
